import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button as TamaguiButton, XStack, YStack } from "tamagui";
import { Calendar, ChevronRight, Compass, MapPin, Users } from "lucide-react";

import { MemberPageScaffold, MemberPill } from "@/components/MemberScaffold";
import { useAuth } from "@/contexts/AuthContext";
import { getTypeConfig, formatAgeRange, formatDateRange, stripAdventureMeta } from "@/lib/adventure-helpers";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";
import { buildMyAdventureBuckets, type MyAdventureStatus } from "../../shared/activities";
import {
  AppButton,
  AppCard,
  AppCardBody,
  AppCardHeader,
  AppEmptyState,
  AppLoadingState,
  AppSection,
  AppSectionHeader,
  AppText,
  kindredFontFamilies,
} from "../../ui-foundation";

type AdventureProfile = Pick<Tables<"profiles">, "display_name">;
type AdventureRow = Tables<"adventures"> & {
  profiles: AdventureProfile | null;
  organiser_name: string | null;
  interested_count: number;
  going_count: number;
  my_adventure_status: MyAdventureStatus;
  is_past: boolean;
};

type AdventureBuckets = {
  organising: AdventureRow[];
  upcoming: AdventureRow[];
  past: AdventureRow[];
};

const initialBuckets: AdventureBuckets = {
  organising: [],
  upcoming: [],
  past: [],
};

const statusTone: Record<MyAdventureStatus, "primary" | "secondary" | "neutral"> = {
  organising: "secondary",
  going: "primary",
  interested: "neutral",
  waitlist: "neutral",
};

const statusLabel: Record<MyAdventureStatus, string> = {
  organising: "Organising",
  going: "Going",
  interested: "Interested",
  waitlist: "Waitlist",
};

const summarizeCount = (adventure: AdventureRow) => {
  if (adventure.interested_count === 0 && adventure.going_count === 0) {
    return null;
  }

  return `${adventure.interested_count} interested · ${adventure.going_count} going`;
};

const buildDescriptionPreview = (description: string | null) => {
  const clean = stripAdventureMeta(description);
  if (!clean) return null;
  return clean.length > 140 ? `${clean.slice(0, 137)}...` : clean;
};

const MyAdventureCard = ({ adventure, onPress }: { adventure: AdventureRow; onPress: () => void }) => {
  const typeConfig = getTypeConfig(adventure.type);
  const description = buildDescriptionPreview(adventure.description);
  const countSummary = summarizeCount(adventure);

  return (
    <TamaguiButton
      unstyled
      type="button"
      width="100%"
      borderWidth={1}
      borderColor="$borderColor"
      borderRadius="$md"
      backgroundColor="$background"
      pressStyle={{ opacity: 0.94 }}
      onPress={onPress}
    >
      <AppCardBody gap="$sm">
        <XStack alignItems="flex-start" gap="$sm">
          <YStack flex={1} minWidth={0} gap={6}>
            <XStack alignItems="flex-start" justifyContent="space-between" gap="$sm">
              <AppText
                flex={1}
                minWidth={0}
                fontFamily={kindredFontFamilies.sansBold}
                fontSize="$md"
                lineHeight="$md"
              >
                {adventure.title}
              </AppText>
              <ChevronRight size={18} className="shrink-0 text-muted-foreground" />
            </XStack>

            <XStack flexWrap="wrap" gap="$xs">
              <MemberPill tone={statusTone[adventure.my_adventure_status]}>
                {statusLabel[adventure.my_adventure_status]}
              </MemberPill>
              <MemberPill>{`${typeConfig.emoji} ${typeConfig.label}`}</MemberPill>
            </XStack>

            <XStack alignItems="center" gap="$xs">
              <Calendar size={14} className="shrink-0 text-muted-foreground" />
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                {formatDateRange(adventure.date_start, adventure.date_end)}
              </AppText>
            </XStack>

            <XStack alignItems="center" gap="$xs">
              <MapPin size={14} className="shrink-0 text-muted-foreground" />
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                {adventure.location}
              </AppText>
            </XStack>

            <XStack alignItems="center" gap="$xs">
              <Users size={14} className="shrink-0 text-muted-foreground" />
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                {formatAgeRange(adventure.min_child_age, adventure.max_child_age)}
              </AppText>
            </XStack>

            {description ? (
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$sm">
                {description}
              </AppText>
            ) : null}

            <XStack flexWrap="wrap" gap="$xs">
              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">
                {`By ${adventure.organiser_name ?? "Kindred member"}`}
              </AppText>
              {countSummary ? (
                <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">
                  {`· ${countSummary}`}
                </AppText>
              ) : null}
            </XStack>
          </YStack>
        </XStack>
      </AppCardBody>
    </TamaguiButton>
  );
};

const AdventureSectionCard = ({
  title,
  subtitle,
  emptyTitle,
  emptyBody,
  adventures,
  onOpen,
}: {
  title: string;
  subtitle: string;
  emptyTitle: string;
  emptyBody: string;
  adventures: AdventureRow[];
  onOpen: (adventureId: string) => void;
}) => (
  <AppCard>
    <AppCardHeader>
      <AppSectionHeader title={title} subtitle={subtitle} />
    </AppCardHeader>
    <AppCardBody>
      {adventures.length === 0 ? (
        <AppEmptyState title={emptyTitle} body={emptyBody} />
      ) : (
        <AppSection>
          {adventures.map((adventure) => (
            <MyAdventureCard
              key={adventure.id}
              adventure={adventure}
              onPress={() => onOpen(adventure.id)}
            />
          ))}
        </AppSection>
      )}
    </AppCardBody>
  </AppCard>
);

const MyAdventures = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [buckets, setBuckets] = useState<AdventureBuckets>(initialBuckets);

  const loadMyAdventures = useCallback(async () => {
    if (!user?.id) {
      setBuckets(initialBuckets);
      setLoading(false);
      return;
    }

    setLoading(true);

    const [{ data: memberships, error: membershipsError }, { data: organisingRows, error: organisingError }] = await Promise.all([
      supabase.from("adventure_members").select("adventure_id, status").eq("user_id", user.id),
      supabase
        .from("adventures")
        .select("*, profiles!adventures_user_id_fkey(display_name)")
        .eq("user_id", user.id)
        .eq("status", "active"),
    ]);

    if (membershipsError) {
      console.error("Could not load linked adventures for My adventures:", membershipsError);
    }

    if (organisingError) {
      console.error("Could not load organiser adventures for My adventures:", organisingError);
    }

    const membershipByAdventureId = Object.fromEntries(
      (memberships ?? []).map((membership) => [membership.adventure_id, membership.status]),
    );
    const memberAdventureIds = Object.keys(membershipByAdventureId);

    const { data: memberRows, error: memberRowsError } = memberAdventureIds.length
      ? await supabase
          .from("adventures")
          .select("*, profiles!adventures_user_id_fkey(display_name)")
          .in("id", memberAdventureIds)
          .eq("status", "active")
      : { data: [] as Array<Tables<"adventures"> & { profiles: AdventureProfile | null }>, error: null };

    if (memberRowsError) {
      console.error("Could not load member adventures for My adventures:", memberRowsError);
    }

    const mergedAdventureMap = new Map<string, Tables<"adventures"> & { profiles: AdventureProfile | null }>();
    for (const adventure of (organisingRows ?? []) as Array<Tables<"adventures"> & { profiles: AdventureProfile | null }>) {
      mergedAdventureMap.set(adventure.id, adventure);
    }
    for (const adventure of (memberRows ?? []) as Array<Tables<"adventures"> & { profiles: AdventureProfile | null }>) {
      mergedAdventureMap.set(adventure.id, adventure);
    }

    const mergedAdventures = Array.from(mergedAdventureMap.values()).map((adventure) => ({
      ...adventure,
      organiser_name: adventure.profiles?.display_name ?? null,
    }));
    const adventureIds = mergedAdventures.map((adventure) => adventure.id);

    const { data: memberCountRows, error: memberCountError } = adventureIds.length
      ? await supabase.from("adventure_members").select("adventure_id, status").in("adventure_id", adventureIds)
      : { data: [] as Array<{ adventure_id: string; status: string }>, error: null };

    if (memberCountError) {
      console.error("Could not load adventure member counts for My adventures:", memberCountError);
    }

    const countMap: Record<string, { interested: number; going: number }> = {};
    for (const row of memberCountRows ?? []) {
      if (!countMap[row.adventure_id]) {
        countMap[row.adventure_id] = { interested: 0, going: 0 };
      }

      if (row.status === "interested") {
        countMap[row.adventure_id].interested += 1;
      }

      if (row.status === "joined") {
        countMap[row.adventure_id].going += 1;
      }
    }

    const adventuresWithCounts = mergedAdventures.map((adventure) => ({
      ...adventure,
      interested_count: countMap[adventure.id]?.interested ?? 0,
      going_count: countMap[adventure.id]?.going ?? 0,
    }));

    setBuckets(buildMyAdventureBuckets(adventuresWithCounts, user.id, membershipByAdventureId));
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    void loadMyAdventures();
  }, [loadMyAdventures]);

  const totalAdventures = useMemo(
    () => buckets.organising.length + buckets.upcoming.length + buckets.past.length,
    [buckets],
  );

  return (
    <MemberPageScaffold
      title="My adventures"
      subtitle="Keep the activities you organise, the plans you are joining, and your past adventures together."
      fallbackPath="/my-kindred"
      action={
        <AppButton type="button" tone="outline" size="sm" onPress={() => navigate("/activities")}>
          <Compass size={14} />
          Browse activities
        </AppButton>
      }
    >
      {loading ? (
        <AppLoadingState
          title="Loading your adventures"
          body="Pulling together the plans you organise, the ones you have joined, and the past ones worth remembering."
        />
      ) : totalAdventures === 0 ? (
        <AppEmptyState
          title="No adventures yet"
          body="When you organise or join activities, they will appear here so your plans stay easy to find."
        />
      ) : (
        <AppSection>
          <AdventureSectionCard
            title="Organising"
            subtitle="The activities you are currently leading or hosting."
            emptyTitle="Nothing to host right now"
            emptyBody="Activities you organise will appear here before they start."
            adventures={buckets.organising}
            onOpen={(adventureId) => navigate(`/adventures/${adventureId}`)}
          />
          <AdventureSectionCard
            title="Coming up"
            subtitle="The activities you have marked as going, interested, or waitlisted."
            emptyTitle="No upcoming plans yet"
            emptyBody="Join, waitlist, or mark interest on an activity and it will stay easy to find here."
            adventures={buckets.upcoming}
            onOpen={(adventureId) => navigate(`/adventures/${adventureId}`)}
          />
          <AdventureSectionCard
            title="Past adventures"
            subtitle="A calm history of the activities you organised or took part in."
            emptyTitle="No adventure history yet"
            emptyBody="Past activities will collect here once a date has passed."
            adventures={buckets.past}
            onOpen={(adventureId) => navigate(`/adventures/${adventureId}`)}
          />
        </AppSection>
      )}
    </MemberPageScaffold>
  );
};

export default MyAdventures;
